import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import Notification from "../components/Notification";
import UpgradePrompt from "../components/UpgradePrompt";
import { useStudioPlan } from "../hooks/useStudioPlan";
import {
  getLocalDateString,
  getLocalYMDFromUTC,
  formatTimeTo12Hour,
} from "../utils/timezone";

/** Same calendar day as selectedDate (YYYY-MM-DD) in the studio timezone. */
const isAttendanceOnSelectedStudioDate = (
  attendedAt: string,
  studioTimezone: string,
  selectedDateYmd: string
): boolean =>
  getLocalYMDFromUTC(attendedAt, studioTimezone) === selectedDateYmd;

const getDayOfWeek = (dateString: string, studioTimezone: string): string => {
  try {
    const [year, month, day] = dateString
      .split("-")
      .map((num) => parseInt(num));
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const options: Intl.DateTimeFormatOptions = {
      timeZone: studioTimezone,
      weekday: "long",
    };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  } catch (error) {
    const fallbackDate = new Date(dateString);
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[fallbackDate.getDay()];
  }
};

// Returns true if the current wall-clock time in the studio timezone is within
// 5 minutes before (or any time after) the shift's start_time.
const canClockIntoShift = (shift: any, studioTimezone: string): boolean => {
  if (!shift?.start_time) return true;
  try {
    const nowStr = new Date().toLocaleTimeString("en-US", {
      timeZone: studioTimezone,
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const [nowH, nowM, nowS] = nowStr.split(":").map(Number);
    const nowMinutes = nowH * 60 + nowM + nowS / 60;
    const [shiftH, shiftM] = shift.start_time.split(":").map(Number);
    const shiftMinutes = shiftH * 60 + shiftM;
    return nowMinutes >= shiftMinutes - 5;
  } catch {
    return true; // fail open
  }
};

const formatWallClockTime = (time: string): string => {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return m === 0
    ? `${h12}${ampm}`
    : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
};

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  searchTerm: 100,
  logoutPassword: 100,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For search terms, preserve spaces but remove dangerous content
  if (fieldType === 'searchTerm') {
    // Only remove dangerous patterns, preserve spaces
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    // Don't trim to preserve user intent
  } else {
    // For other fields (like passwords), use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'searchTerm':
      // Check for dangerous patterns in search terms
      const searchDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !searchDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'logoutPassword':
      // Check for dangerous patterns in password
      const passwordDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !passwordDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`StudentSignIn Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  }
};

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-white`;
};

export default function StudentSignIn() {
  const [searchTerm, setSearchTerm] = useState("");
  const [studentNumber, setStudentNumber] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [studioTimezone, setStudioTimezone] = useState("America/New_York");
  const [selectedDate, setSelectedDate] = useState("");
  const [showLeadsInSignIn, setShowLeadsInSignIn] = useState(true);
  const navigate = useNavigate();
  const [kioskStudioId, setKioskStudioId] = useState<string | null>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutPassword, setLogoutPassword] = useState("");
  const [logoutError, setLogoutError] = useState("");
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [studioName, setStudioName] = useState<string>("");
  const [selectedClassOrEvent, setSelectedClassOrEvent] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<"class" | "event" | null>(
    null
  );
  const [events, setEvents] = useState<any[]>([]);
  const [selectedStudentAssignedClasses, setSelectedStudentAssignedClasses] = useState<string[]>([]);
  const [loadingAssignedClasses, setLoadingAssignedClasses] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [showWelcome, setShowWelcome] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean,
    message: string,
    title?: string,
  }>({
    show: false,
    message: "",
  });

  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // Rate limiting state
  const [isCheckingIn, setIsCheckingIn] = useState(false);

  // Staff clock-in/out state
  const [selectedStaff, setSelectedStaff] = useState<any | null>(null);
  const [selectedStaffClass, setSelectedStaffClass] = useState<any | null>(null);
  const [isStaffClockin, setIsStaffClockin] = useState(false);
  const [isLookingUpStaff, setIsLookingUpStaff] = useState(false);
  // Multi-role: when staff has >1 role, hold them here until role is picked
  const [pendingStaffForRolePicker, setPendingStaffForRolePicker] = useState<any | null>(null);
  const [selectedRoleAssignment, setSelectedRoleAssignment] = useState<any | null>(null);
  // Shift picker: shown after role selection when staff has classes today
  const [pendingStaffForShiftPicker, setPendingStaffForShiftPicker] = useState<any | null>(null);
  const [staffPayrollBlocked, setStaffPayrollBlocked] = useState<{ name: string } | null>(null);

  const { user, session, getAuthToken } = useAuth();
  const { hasCapability } = useStudioPlan();
  const canUseStaffPayroll = hasCapability("staffPayroll");
  const location = useLocation();



  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value, 'searchTerm');
    
    // Allow empty search terms (for clearing the search)
    if (sanitizedValue === '') {
      setSearchTerm(sanitizedValue);
      return;
    }
    
    // Enhanced validation only for non-empty values
    if (!validateInput(sanitizedValue, 'searchTerm')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'searchTerm', value: sanitizedValue.substring(0, 50) + '...' });
      setNotification({
        show: true,
        message: "Invalid search term. Please check your input and try again.",
        title: "Validation Error",
      });
      return;
    }

    // Check length limits
    if (sanitizedValue.length > MAX_LENGTHS.searchTerm) {
      setNotification({
        show: true,
        message: `Search term too long. Maximum ${MAX_LENGTHS.searchTerm} characters.`,
        title: "Validation Error",
      });
      return;
    }

    setSearchTerm(sanitizedValue);
  };

  // Handle keypad number press
  const handleKeypadPress = (num: string) => {
    if (studentNumber.length < 6) {
      setStudentNumber(studentNumber + num);
    }
  };

  // Handle keypad clear (dismiss any previous notification so it doesn’t persist for the next student)
  const handleKeypadClear = () => {
    setStudentNumber("");
    setSelectedStudent(null);
    setSelectedStaff(null);
    setSelectedStaffClass(null);
    setPendingStaffForRolePicker(null);
    setPendingStaffForShiftPicker(null);
    setStaffPayrollBlocked(null);
    setSelectedRoleAssignment(null);
    setIsLookingUpStaff(false);
    setShowWelcome(false);
    setNotification((prev) => ({ ...prev, show: false }));
  };

  // Handle keypad backspace (dismiss any previous notification)
  const handleKeypadBackspace = () => {
    setStudentNumber(studentNumber.slice(0, -1));
    setSelectedStudent(null);
    setShowWelcome(false);
    setNotification((prev) => ({ ...prev, show: false }));
  };

  // Handle keypad enter - search for student by number, then staff if not found
  const handleKeypadEnter = async () => {
    if (!studentNumber) return;

    const numValue = parseInt(studentNumber, 10);

    // 1. Check students first
    const foundStudent = students.find(
      (s) => s.student_number !== null &&
             s.student_number !== undefined &&
             Number(s.student_number) === numValue
    );

    if (foundStudent) {
      setSelectedStudent({ ...foundStudent, type: "student" });
      setSelectedStaff(null);
      setShowWelcome(true);
      setNotification((prev) => ({ ...prev, show: false }));

      setLoadingAssignedClasses(true);
      try {
        const studentData = students.find(s => s.id === foundStudent.id);
        setSelectedStudentAssignedClasses(studentData?.assigned_classes || []);
      } catch {
        setSelectedStudentAssignedClasses([]);
      } finally {
        setLoadingAssignedClasses(false);
      }

      setTimeout(() => setShowWelcome(false), 2000);
      return;
    }

    // 2. Check staff via backend lookup
    const authToken = getAuthToken();
    if (!authToken || !kioskStudioId) {
      setNotification({ show: true, title: "Not Found", message: `Number ${studentNumber} not found. Please try again.` });
      setStudentNumber("");
      return;
    }

    setIsLookingUpStaff(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-by-number/${kioskStudioId}/${numValue}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (res.ok) {
        const foundStaff = await res.json();
        setSelectedStudent(null);
        setNotification((prev) => ({ ...prev, show: false }));
        setStudentNumber("");
        setSelectedStaffClass(null);
        setSelectedRoleAssignment(null);

        if (!canUseStaffPayroll) {
          setStaffPayrollBlocked({ name: foundStaff.name });
          return;
        }

        // Already clocked in — go straight to clock-out screen, no pickers needed
        if (foundStaff.clocked_in_at) {
          setSelectedStaff(foundStaff);
        } else if (
          Array.isArray(foundStaff.role_assignments) &&
          foundStaff.role_assignments.length > 1
        ) {
          // Multiple roles — show role picker first, shift picker comes after
          setPendingStaffForRolePicker(foundStaff);
        } else {
          // Single role — auto-select it, then filter shifts to that role
          const autoRole = Array.isArray(foundStaff.role_assignments) && foundStaff.role_assignments.length === 1
            ? foundStaff.role_assignments[0]
            : null;
          if (autoRole) setSelectedRoleAssignment(autoRole);
          proceedAfterRoleSelection(foundStaff, undefined, autoRole ?? undefined);
        }
      } else {
        setNotification({ show: true, title: "Not Found", message: `Number ${studentNumber} not found. Please try again.` });
        setStudentNumber("");
      }
    } catch {
      setNotification({ show: true, title: "Not Found", message: `Number ${studentNumber} not found. Please try again.` });
      setStudentNumber("");
    } finally {
      setIsLookingUpStaff(false);
    }
  };

  // Format "HH:MM:SS" → "10:30am"
  const formatClassTime = formatWallClockTime;

  // Look up today's shift for a staff member, then proceed to clock-in.
  // roleAssignment: the role the staff member selected — used to filter shifts to
  //   only those scheduled under that role, preventing accidental wrong-role clock-ins.
  // onDone is called after state is set so callers can clear their own UI state
  //   (e.g. role picker) only after the transition is ready — no numpad flash.
  const proceedAfterRoleSelection = async (staff: any, onDone?: () => void, roleAssignment?: any) => {
    const authToken = getAuthToken();
    if (!authToken || !kioskStudioId) {
      setSelectedStaff(staff);
      onDone?.();
      return;
    }
    const today = getLocalDateString(studioTimezone);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-shifts/${kioskStudioId}/for-staff/${staff.id}?date=${today}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const allShifts: any[] = res.ok ? await res.json() : [];

      // If a role was selected, only show shifts assigned to that role.
      // This prevents staff from accidentally clocking into the wrong role's shift.
      const shifts = roleAssignment?.staff_role_id
        ? allShifts.filter((s: any) => s.staff_role_id === roleAssignment.staff_role_id)
        : allShifts;

      if (shifts.length === 0) {
        setSelectedStaff({ ...staff, todayShift: null });
      } else if (shifts.length === 1) {
        setSelectedStaff({ ...staff, todayShift: shifts[0] });
      } else {
        setPendingStaffForShiftPicker({ ...staff, todayShifts: shifts });
      }
      onDone?.();
    } catch {
      setSelectedStaff(staff);
    }
  };

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        // eslint-disable-next-line no-console
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  useEffect(() => {
    if (!kioskStudioId) return;
    fetchCsrfToken();
    const loadData = async () => {
      const authToken = getAuthToken();
      if (!user?.id || !authToken) return;
      try {
        // Fetch studio data
        const studioResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!studioResponse.ok) {
          throw new Error("Failed to fetch studio data");
        }
        const studioData = await studioResponse.json();
        setStudioName(studioData.name || "Martial Arts Studio");
        setStudioTimezone(studioData.timezone || "America/New_York");
        setSelectedDate(getLocalDateString(studioData.timezone || "America/New_York"));

        // Fetch lead visibility setting
        try {
          const settingResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/lead-visibility/${studioData.id}`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          );
          if (!settingResponse.ok) {
            throw new Error("Failed to fetch lead visibility setting");
          }
          const setting = await settingResponse.json();
          setShowLeadsInSignIn(
            setting && typeof setting.is_enabled === "boolean"
              ? setting.is_enabled
              : true
          );
        } catch (err) {
          setShowLeadsInSignIn(true);
        }

        // Fetch students (all pages), leads, and classes
        const studentsFirstPageResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioData.id}?page=1&limit=10000`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        const [leadsResponse, classesResponse] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/${studioData.id}?limit=10000`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          ),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/supabase/classes/${studioData.id}`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          ),
        ]);

        if (!studentsFirstPageResponse.ok || !leadsResponse.ok || !classesResponse.ok) {
          throw new Error("Failed to fetch data");
        }

        const studentsFirstPage = await studentsFirstPageResponse.json();
        const [leadsData, classesData] = await Promise.all([
          leadsResponse.json(),
          classesResponse.json(),
        ]);

        // Assemble all students across all pages
        let allStudents = studentsFirstPage.data || [];
        const totalPages = studentsFirstPage.pagination?.totalPages || 1;
        const pageLimit = studentsFirstPage.pagination?.limit || 15;
        if (totalPages > 1) {
          const pageFetches: Promise<Response>[] = [];
          for (let p = 2; p <= totalPages; p++) {
            pageFetches.push(
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/${studioData.id}?page=${p}&limit=${pageLimit}`,
                {
                  headers: {
                    Authorization: `Bearer ${authToken}`,
                  },
                }
              )
            );
          }
          const pageResponses = await Promise.all(pageFetches);
          const pageJsons = await Promise.all(pageResponses.map(r => r.json()));
          for (const pj of pageJsons) {
            allStudents = allStudents.concat(pj.data || []);
          }
        }

        setStudents(allStudents);
        setLeads(leadsData.data || []);
        setClasses(classesData || []);
        


        // Fetch events
        const eventsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${studioData.id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!eventsResponse.ok) {
          throw new Error("Failed to fetch events");
        }
        const eventsData = await eventsResponse.json();
        setEvents(eventsData || []);

        // Fetch today's attendance records to prevent duplicates
        const todayDateString = getLocalDateString(studioData.timezone || "America/New_York");
        const attendanceResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/${studioData.id}?date=${todayDateString}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (attendanceResponse.ok) {
          const attendanceData = await attendanceResponse.json();
          setAttendanceRecords(attendanceData || []);
        }
      } catch (error) {
        setNotification({
          show: true,
          message: "Failed to load data",
          title: "Error",
        });
      }
    };

    loadData();
  }, [kioskStudioId, navigate, user, getAuthToken]);

  useEffect(() => {
    // Save previous styles
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.padding;
    const prevMargin = document.body.style.margin;

    document.body.style.overflow = "hidden";
    document.body.style.padding = "0";
    document.body.style.margin = "0";

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.padding = prevPadding;
      document.body.style.margin = prevMargin;
    };
  }, []);


  
  // Filter classes for today in studio timezone and only show assigned classes for students
  const todaysClasses = classes
    .filter((classItem) => {
      const dayOfWeek = getDayOfWeek(selectedDate, studioTimezone);
      const isToday = classItem.days && classItem.days.includes(dayOfWeek);

      // If no student is selected, show all today's classes
      if (!selectedStudent) return isToday;
      
      // If it's a lead, show all today's classes
      if (selectedStudent.type === "lead") return isToday;
      
      // If it's a student, only show classes they're assigned to
      if (selectedStudent.type === "student") {
        const isAssigned = selectedStudentAssignedClasses.includes(classItem.id);
        return isToday && isAssigned;
      }
      
      return isToday;
    })
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  // Filter events for today in studio timezone
  const todaysEvents = events
    ? events.filter((eventItem: any) => {
        // Check if the selected date falls within the event's date range
        return selectedDate >= eventItem.from_date && selectedDate <= eventItem.end_date;
      })
    : [];

  // Combine students and leads for search
  const filteredPeople = showLeadsInSignIn
    ? [
        ...students.map((student) => ({ ...student, type: "student" })),
        ...leads.map((lead) => ({ ...lead, type: "lead" })),
      ].filter((person) =>
        person.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : students
        .map((student) => ({ ...student, type: "student" }))
        .filter((person) =>
          person.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

  const handleStudentSelect = async (person: any) => {
    setSelectedStudent(person);
    setSelectedClassOrEvent(null);
    setSelectedType(null);
    setSearchTerm("");
    
    // If it's a student, get their assigned classes from the existing students data
    if (person.type === "student") {
      setLoadingAssignedClasses(true);
      try {
        // Find the student in the existing students array
        const studentData = students.find(s => s.id === person.id);
        const assignedClasses = studentData?.assigned_classes || [];
        setSelectedStudentAssignedClasses(assignedClasses);
      } catch {
        setSelectedStudentAssignedClasses([]);
      } finally {
        setLoadingAssignedClasses(false);
      }
    } else {
      // For leads, clear assigned classes
      setSelectedStudentAssignedClasses([]);
      setLoadingAssignedClasses(false);
    }
  };

  // Staff clock in
  const handleStaffClockIn = async () => {
    if (!selectedStaff || !kioskStudioId) return;
    if (isStaffClockin) return;
    setIsStaffClockin(true);
    try {
      const authToken = getAuthToken();
      if (!authToken) throw new Error("Not authenticated");

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/clock-in`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            staff_id: selectedStaff.id,
            studio_id: kioskStudioId,
            shift_id: selectedStaff.todayShift?.id || null,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to clock in");
      }

      const shiftLabel = selectedStaff.todayShift
        ? ` · ${formatClassTime(selectedStaff.todayShift.start_time)}–${formatClassTime(selectedStaff.todayShift.end_time)}`
        : "";
      setNotification({
        show: true,
        title: "Clocked In",
        message: `${selectedStaff.name} is now clocked in${shiftLabel}.`,
      });
    } catch (err) {
      setNotification({
        show: true,
        title: "Error",
        message: err instanceof Error ? err.message : "Failed to clock in",
      });
    } finally {
      setIsStaffClockin(false);
      setSelectedStaff(null);
      setSelectedStaffClass(null);
      setSelectedRoleAssignment(null);
    }
  };

  // Staff clock out
  const handleStaffClockOut = async () => {
    if (!selectedStaff || !kioskStudioId) return;
    if (isStaffClockin) return;
    setIsStaffClockin(true);
    try {
      const authToken = getAuthToken();
      if (!authToken) throw new Error("Not authenticated");

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/clock-out`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ staff_id: selectedStaff.id, studio_id: kioskStudioId }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to clock out");
      }

      const result = await res.json();
      const hrs = result.hours_worked ?? 0;
      const totalMins = Math.round(hrs * 60);
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      const sessionLabel = h > 0 ? `${h}h ${m}m` : `${m}m`;

      setNotification({
        show: true,
        title: "Clocked Out",
        message: `${selectedStaff.name} clocked out. Session: ${sessionLabel}.`,
      });
    } catch (err) {
      setNotification({
        show: true,
        title: "Error",
        message: err instanceof Error ? err.message : "Failed to clock out",
      });
    } finally {
      setIsStaffClockin(false);
      setSelectedStaff(null);
      setSelectedStaffClass(null);
      setSelectedRoleAssignment(null);
    }
  };

  const handleClassSelect = (classItem: any) => {
    setSelectedClassOrEvent(classItem);
    setSelectedType("class");
  };

  const handleEventSelect = (eventItem: any) => {
    setSelectedClassOrEvent(eventItem);
    setSelectedType("event");
  };

  const handleConfirmCheckIn = async () => {
    if (!selectedStudent || !selectedClassOrEvent || !selectedType) return;

    // Students must have an active membership to check in
    if (selectedStudent.type === "student") {
      const hasActiveMembership =
        (selectedStudent.membership_type_ids && selectedStudent.membership_type_ids.length > 0) ||
        !!selectedStudent.membership_type;
      if (!hasActiveMembership) {
        setNotification({
          show: true,
          message: "You need an active membership to check in. Please see the front desk to add or renew your membership.",
          title: "Membership Required",
        });
        return;
      }
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before checking in again",
        title: "Rate Limited",
      });
      return;
    }

    // Check if student/lead is already checked in for this class/event today (studio calendar day)
    const isAlreadyCheckedIn = attendanceRecords.some((record) => {
      const isSameDay = isAttendanceOnSelectedStudioDate(
        record.attended_at,
        studioTimezone,
        selectedDate
      );
      const personMatches =
        selectedStudent.type === "student"
          ? record.student_id === selectedStudent.id
          : record.lead_id === selectedStudent.id;
      return (
        isSameDay &&
        personMatches &&
        record.class_id === (selectedType === "class" ? selectedClassOrEvent.id : null) &&
        record.event_id === (selectedType === "event" ? selectedClassOrEvent.id : null)
      );
    });

    if (isAlreadyCheckedIn) {
      setNotification({
        show: true,
        message: `${selectedStudent.name} is already checked in for this ${selectedType} today`,
        title: "Already Checked In",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'checkIn' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with check-in');
      }
    }

    // Prevent multiple simultaneous check-ins
    if (isCheckingIn) return;

    setIsCheckingIn(true);
    setLastSubmissionTime(now);

    try {
      // Determine if it's a student or lead
      const isStudent = selectedStudent.type === "student";
      const isLead = selectedStudent.type === "lead";

      const [hours, minutes, seconds] = (
        selectedClassOrEvent.start_time || "00:00:00"
      )
        .split(":")
        .map(Number);
      const timeString = `${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}:${String(seconds || 0).padStart(2, "0")}`;

      const payload = {
        student_id: isStudent ? selectedStudent.id : null,
        lead_id: isLead ? selectedStudent.id : null,
        class_id: selectedType === "class" ? selectedClassOrEvent.id : null,
        event_id: selectedType === "event" ? selectedClassOrEvent.id : null,
        studio_id: kioskStudioId,
        date_string: selectedDate,
        time_string: timeString,
        timezone: studioTimezone,
      };

      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication token not found.");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/check-in`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            ...payload,
            csrfToken,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || errorData.error || (response.status === 409 ? "Already checked in for this class/event today" : "Failed to check in");
        throw new Error(message);
      }

      const checkInBody = await response.json();
      const { updatedStudent: _updatedStudent, ...newAttendanceRow } = checkInBody;
      setAttendanceRecords((prev) => [...prev, newAttendanceRow]);

      setNotification({
        show: true,
        message: `${selectedStudent.name} checked in for ${
          selectedType === "class"
            ? selectedClassOrEvent.class_name
            : selectedClassOrEvent.event_name
        }!`,
        title: "Success",
      });
      
      // Reset everything after successful check-in
      setSelectedStudent(null);
      setSelectedClassOrEvent(null);
      setSelectedType(null);
      setStudentNumber("");
      setSelectedStudentAssignedClasses([]);
      setShowWelcome(false);
    } catch (error) {
      logSecurityEvent('CHECK_IN_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studentId: selectedStudent?.id,
        classId: selectedClassOrEvent?.id,
        type: selectedType
      });
      
      // Show the actual error message from the backend if available
      const errorMessage = error instanceof Error ? error.message : "Error checking in. Please try again.";
      const isMembershipError = /membership/i.test(errorMessage);
      const isAlreadyCheckedIn = errorMessage.includes("Already checked in");
      const title = isMembershipError ? "Membership Required" : isAlreadyCheckedIn ? "Already Checked In" : "Error";
      setNotification({
        show: true,
        message: errorMessage,
        title,
      });
    } finally {
      setIsCheckingIn(false);
    }
  };

  // Prevent page refresh and block F5/Ctrl+R/Cmd+R
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      // F5, Ctrl+R, Cmd+R
      if (
        e.key === "F5" ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r")
      ) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Always disable browser back button on this page
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      window.history.pushState(null, document.title, window.location.href);
    };
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Fetch studio info for kiosk password verification if not in kiosk mode
  useEffect(() => {
    if (kioskStudioId) return;
    const fetchStudio = async () => {
      try {
        const authToken = getAuthToken();
        if (!authToken) return;
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!res.ok) return;
        const studio = await res.json();
        if (studio?.id) setKioskStudioId(studio.id);
      } catch {}
    };
    fetchStudio();
  }, [kioskStudioId, getAuthToken]);

  // Lock user into student sign-in page using sessionStorage
  useEffect(() => {
    // Set the lock when entering this page
    sessionStorage.setItem("lockStudentSignIn", "true");
    // On mount, if not on /app/student-sign-in but lock is set, redirect
    if (
      sessionStorage.getItem("lockStudentSignIn") === "true" &&
      location.pathname !== "/app/student-sign-in"
    ) {
      navigate("/app/student-sign-in", { replace: true });
    }
    // Clean up: remove lock on unmount (if you want to allow navigation away)
    return () => {
      // sessionStorage.removeItem('lockStudentSignIn'); // Only remove on logout
    };
  }, [location.pathname, navigate]);

  // Show loading screen until studio data is loaded
  if (!kioskStudioId || !studioName) {
    return (
      <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-900 m-0 p-0">
        <div className="w-full max-w-2xl mx-auto p-6">
          <div className="text-center pt-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Loading...
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              Setting up your sign-in experience
            </p>
          </div>
        {/* Always show Logout Button even during loading */}
        <button
          className="fixed bottom-8 left-8 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded shadow-lg z-50"
          onClick={() => setShowLogoutModal(true)}
        >
          <span className="pointer-events-none">Logout</span>
        </button>
        {/* Logout Modal */}
        {showLogoutModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-sm w-full">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Exit Kiosk Mode
              </h2>
              <p className="mb-2 text-gray-700 dark:text-gray-300">
                Enter the kiosk password to exit kiosk mode and return to the
                admin dashboard.
              </p>
              <input
                type="password"
                maxLength={MAX_LENGTHS.logoutPassword}
                value={logoutPassword}
                onChange={(e) => {
                  const sanitizedValue = sanitizeInput(e.target.value, 'logoutPassword');
                  if (sanitizedValue.length <= MAX_LENGTHS.logoutPassword) {
                    setLogoutPassword(sanitizedValue);
                  }
                }}
                className={getInputClassName(
                  "w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 mb-2"
                )}
                placeholder="Kiosk Password"
                autoFocus
              />
              {logoutError && (
                <div className="text-red-600 dark:text-red-400 text-sm mb-2">
                  {logoutError}
                </div>
              )}
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
                  onClick={() => {
                    setShowLogoutModal(false);
                    setLogoutPassword("");
                    setLogoutError("");
                  }}
                  disabled={logoutLoading}
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={async () => {
                    setLogoutError("");
                    setLogoutLoading(true);
                    try {
                      if (!kioskStudioId) throw new Error("Studio not found");

                      // Enhanced password validation
                      if (!validateInput(logoutPassword, 'logoutPassword')) {
                        logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'logoutPassword', value: logoutPassword.substring(0, 10) + '...' });
                        setLogoutError("Invalid password format. Please check your input.");
                        setLogoutLoading(false);
                        return;
                      }

                      const token = getAuthToken();
                      if (!token) {
                        setLogoutError(
                          "You must be logged in to exit kiosk mode."
                        );
                        setLogoutLoading(false);
                        return;
                      }

                      const response = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${kioskStudioId}/verify-kiosk-password`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                            "X-CSRF-Token": csrfToken,
                          },
                          body: JSON.stringify({
                            password: logoutPassword,
                            csrfToken,
                          }),
                        }
                      );
                      const data = await response.json();
                      if (!response.ok) {
                        throw new Error(
                          data.error || "Failed to verify password"
                        );
                      }
                      if (!data.isMatch) {
                        setLogoutError("Incorrect kiosk password.");
                        setLogoutLoading(false);
                        return;
                      }
                      setShowLogoutModal(false);
                      setLogoutPassword("");
                      setLogoutError("");
                      setLogoutLoading(false);
                      sessionStorage.removeItem("lockStudentSignIn");

                      setTimeout(() => {
                        navigate("/app", { replace: true });
                      }, 100);
                    } catch (err) {
                      logSecurityEvent('LOGOUT_ERROR', { 
                        error: err instanceof Error ? err.message : 'Unknown error',
                        studioId: kioskStudioId
                      });
                      setLogoutError(
                        err instanceof Error
                          ? err.message
                          : "Error verifying password."
                      );
                      setLogoutLoading(false);
                    }
                  }}
                  disabled={logoutLoading}
                >
                  <span className="pointer-events-none">{logoutLoading ? "Logging out..." : "Logout"}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-900 m-0 p-0 relative flex items-center justify-center">
      <div className="w-full max-w-2xl mx-auto p-6 relative">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        {/* Always show Logout Button */}
        <button
          className="fixed bottom-8 left-8 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded shadow-lg z-50"
          onClick={() => setShowLogoutModal(true)}
        >
          <span className="pointer-events-none">Logout</span>
        </button>
        {/* Welcome Message */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            Welcome to {studioName}
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {canUseStaffPayroll
              ? "Please enter your student or staff number."
              : "Please enter your student number."}
          </p>
        </div>

        {/* Staff payroll unavailable on free plan */}
        {staffPayrollBlocked && !selectedStudent && (
          <div className="max-w-lg mx-auto mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700 mb-4 text-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                Hi, {staffPayrollBlocked.name}!
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Staff clock-in is not enabled on this plan.
              </p>
            </div>
            <UpgradePrompt capability="staffPayroll" hideTitle className="text-left" />
            <button
              type="button"
              onClick={handleKeypadClear}
              className="mt-4 w-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl p-4 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              Back
            </button>
          </div>
        )}

        {/* Show Welcome Message */}
        {showWelcome && selectedStudent && (
          <div className="max-w-2xl mx-auto mb-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2 border-green-500 dark:border-green-600 rounded-2xl p-8 shadow-xl animate-pulse">
            <div className="text-center">
              <CheckCircleIcon className="h-16 w-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Welcome, {selectedStudent.name}!
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Please select your class below
              </p>
            </div>
          </div>
        )}

        {/* Role Picker — shown when staff has multiple roles and is clocking in */}
        {pendingStaffForRolePicker && !selectedStudent && !selectedStaff && (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border-2 border-indigo-400 dark:border-indigo-500 mb-4 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                Welcome, {pendingStaffForRolePicker.name}!
              </div>
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                Which role are you clocking in as?
              </div>
              <div className="space-y-3">
                {pendingStaffForRolePicker.role_assignments.map((assignment: any) => (
                  <button
                    key={assignment.staff_role_id}
                    type="button"
                    onClick={() => {
                      setSelectedRoleAssignment(assignment);
                      const staff = pendingStaffForRolePicker;
                      proceedAfterRoleSelection(staff, () => setPendingStaffForRolePicker(null), assignment);
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-lg font-bold rounded-2xl p-4 shadow-lg transition-all duration-200 active:scale-95"
                  >
                    <span className="pointer-events-none">
                      {assignment.staff_roles?.name ?? "Unknown Role"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setPendingStaffForRolePicker(null); handleKeypadClear(); }}
              className="w-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl p-4 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Shift picker — shown when staff has multiple shifts scheduled today */}
        {pendingStaffForShiftPicker && (
          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border-2 border-indigo-400 dark:border-indigo-500 mb-6 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                Welcome, {pendingStaffForShiftPicker.name}!
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">Which shift are you clocking in for?</p>
              <div className="flex flex-col gap-3">
                {(pendingStaffForShiftPicker.todayShifts || []).map((shift: any) => {
                  const fmt = (t: string) => {
                    const [h, m] = t.split(":").map(Number);
                    const ampm = h >= 12 ? "pm" : "am";
                    return `${h % 12 || 12}:${String(m).padStart(2, "0")}${ampm}`;
                  };
                  return (
                    <button
                      key={shift.id}
                      type="button"
                      onClick={() => {
                        const staff = pendingStaffForShiftPicker;
                        setPendingStaffForShiftPicker(null);
                        setSelectedStaff({ ...staff, todayShift: shift });
                      }}
                      className="w-full bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-xl px-4 py-3 text-left hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
                    >
                      <span className="font-semibold text-indigo-700 dark:text-indigo-300">
                        {fmt(shift.start_time)}–{fmt(shift.end_time)}
                      </span>
                      {shift.staff_roles?.name && (
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{shift.staff_roles.name}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-3">
              {Array.isArray(pendingStaffForShiftPicker.role_assignments) &&
               pendingStaffForShiftPicker.role_assignments.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const staff = pendingStaffForShiftPicker;
                    setPendingStaffForShiftPicker(null);
                    setSelectedRoleAssignment(null);
                    setPendingStaffForRolePicker(staff);
                  }}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl p-4 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  ← Back
                </button>
              )}
              <button
                type="button"
                onClick={() => { setPendingStaffForShiftPicker(null); handleKeypadClear(); }}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl p-4 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Staff Clock-In / Clock-Out UI */}
        {selectedStaff && !selectedStudent && (() => {
          if (selectedStaff.clocked_in_at) {
            // Already clocked in — show clock-out option
            const clockedInTime = new Date(selectedStaff.clocked_in_at);
            const clockedInLabel = clockedInTime.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
            return (
              <div className="max-w-md mx-auto">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border-2 border-amber-400 dark:border-amber-500 mb-6 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    Welcome back, {selectedStaff.name}!
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                    Clocked in at {clockedInLabel}
                  </div>
                  <button
                    type="button"
                    disabled={isStaffClockin}
                    onClick={handleStaffClockOut}
                    className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xl font-bold rounded-2xl p-5 shadow-lg transition-all duration-200 active:scale-95"
                  >
                    <span className="pointer-events-none">
                      {isStaffClockin ? "Clocking Out..." : "Clock Out"}
                    </span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleKeypadClear}
                  className="w-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl p-4 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            );
          }

          // Not clocked in — show clock-in button
          return (
            <div className="max-w-md mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border-2 border-indigo-400 dark:border-indigo-500 mb-4 text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  Welcome, {selectedStaff.name}!
                </div>

                {/* Shift + role confirmation */}
                <div className="mt-3 mb-5 space-y-2">
                  {selectedStaff.todayShift ? (
                    <div>
                      <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-700 px-4 py-1.5">
                        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                          {formatClassTime(selectedStaff.todayShift.start_time)}–{formatClassTime(selectedStaff.todayShift.end_time)}
                        </span>
                        {selectedStaff.todayShift.staff_roles?.name && (
                          <span className="text-xs text-indigo-500 dark:text-indigo-400">
                            {selectedStaff.todayShift.staff_roles.name}
                          </span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 px-4 py-1.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">No shift scheduled today</span>
                      </span>
                    </div>
                  )}
                  {selectedRoleAssignment && !selectedStaff.todayShift && (
                    <div>
                      <span className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-700 px-4 py-1.5">
                        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                          {selectedRoleAssignment.staff_roles?.name ?? "Instructor"}
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                {(() => {
                  const shiftReady = !selectedStaff.todayShift || canClockIntoShift(selectedStaff.todayShift, studioTimezone);
                  return (
                    <>
                      {!shiftReady && selectedStaff.todayShift && (() => {
                        const [h, m] = selectedStaff.todayShift.start_time.split(":").map(Number);
                        const totalMins = h * 60 + m - 5;
                        const availH = Math.floor(totalMins / 60);
                        const availM = totalMins % 60;
                        const availTimeStr = `${String(availH).padStart(2, "0")}:${String(availM).padStart(2, "0")}:00`;
                        return (
                          <p className="text-center text-sm text-amber-600 dark:text-amber-400 mb-2">
                            Clock-in available at{" "}
                            {formatClassTime(availTimeStr)}{" "}
                            (5 min early check-in)
                          </p>
                        );
                      })()}
                      <button
                        type="button"
                        disabled={isStaffClockin || !selectedStaff.todayShift || !shiftReady}
                        onClick={handleStaffClockIn}
                        className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xl font-bold rounded-2xl p-5 shadow-lg transition-all duration-200 active:scale-95"
                      >
                        <span className="pointer-events-none">
                          {isStaffClockin ? "Clocking In..." : "Clock In"}
                        </span>
                      </button>
                    </>
                  );
                })()}
              </div>
              <div className="flex gap-3">
                {selectedStaff.role_assignments?.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      setPendingStaffForRolePicker(selectedStaff);
                      setSelectedStaff(null);
                      setSelectedRoleAssignment(null);
                    }}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl p-4 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                  >
                    ← Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleKeypadClear}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl p-4 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        })()}

        {/* Student Number Display and Keypad */}
        {!selectedStudent && !selectedStaff && !pendingStaffForRolePicker && !pendingStaffForShiftPicker && (
          <div className="max-w-md mx-auto">
            {/* Number Display */}
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border-2 border-gray-300 dark:border-gray-600">
              <div className="text-center">
                <div className="text-5xl font-bold text-gray-900 dark:text-white tracking-widest min-h-[60px] flex items-center justify-center">
                  {studentNumber || "---"}
                </div>
              </div>
            </div>

            {/* Numeric Keypad */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  onClick={() => handleKeypadPress(num.toString())}
                  className="bg-white dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 text-gray-900 dark:text-white text-3xl font-bold rounded-2xl p-8 shadow-lg border-2 border-gray-300 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 transition-all duration-200 active:scale-95"
                >
                  {num}
                </button>
              ))}
              
              {/* Bottom Row: Clear, 0, Backspace */}
              <button
                onClick={handleKeypadClear}
                className="bg-red-500 hover:bg-red-600 text-white text-lg font-bold rounded-2xl p-8 shadow-lg border-2 border-red-600 transition-all duration-200 active:scale-95"
              >
                Clear
              </button>
              <button
                onClick={() => handleKeypadPress("0")}
                className="bg-white dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 text-gray-900 dark:text-white text-3xl font-bold rounded-2xl p-8 shadow-lg border-2 border-gray-300 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 transition-all duration-200 active:scale-95"
              >
                0
              </button>
              <button
                onClick={handleKeypadBackspace}
                className="bg-gray-500 hover:bg-gray-600 text-white text-lg font-bold rounded-2xl p-8 shadow-lg border-2 border-gray-600 transition-all duration-200 active:scale-95"
              >
                ⌫
              </button>
            </div>

            {/* Enter Button */}
            <button
              onClick={handleKeypadEnter}
              disabled={!studentNumber || isLookingUpStaff}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-2xl font-bold rounded-2xl p-6 shadow-lg border-2 border-green-700 disabled:border-gray-500 transition-all duration-200 active:scale-95"
            >
              {isLookingUpStaff ? "Looking up..." : "Enter"}
            </button>
          </div>
        )}

        {/* Student/Lead List - Hidden when using keypad */}
        {false && searchTerm && (
          <div className="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 shadow-xl mt-2 backdrop-blur-sm">
            {/* Students Section */}
            <div className="px-6 py-3 text-sm font-semibold text-indigo-700 dark:text-indigo-300 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 sticky top-0 z-10 rounded-t-xl border-b border-indigo-200 dark:border-indigo-700 flex items-center">
              <UserGroupIcon className="h-4 w-4 mr-2" />
              Students
            </div>
            {filteredPeople.filter((p) => p.type === "student").length ===
              0 && (
              <div className="px-6 py-8 text-center">
                <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">🔍</div>
                <div className="text-gray-500 dark:text-gray-400 text-sm">
                  No students found matching "{searchTerm}"
                </div>
              </div>
            )}
            {filteredPeople
              .filter((p) => p.type === "student")
              .map((person) => (
                <div
                  key={person.id}
                  className={`group flex items-center justify-between px-6 py-4 cursor-pointer border-l-4 transition-all duration-200 hover:shadow-md ${
                    selectedStudent?.id === person.id &&
                    selectedStudent?.type === person.type
                      ? "bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-l-indigo-500 text-gray-900 dark:text-white font-semibold shadow-md"
                      : "bg-white dark:bg-gray-700 border-l-transparent text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-l-gray-300 dark:hover:border-l-gray-500"
                  }`}
                  onClick={() => handleStudentSelect(person)}
                  style={{ minHeight: 56 }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-gray-900 dark:text-white text-base truncate">
                          {person.name}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                          Student
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 mt-1">
                        {person.belt && (
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {person.belt} Belt
                          </span>
                        )}
                        {person.interest && (
                          <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            {person.interest}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors" />
                  </div>
                </div>
              ))}
            {/* Leads Section */}
            {showLeadsInSignIn && (
              <>
                <div className="px-6 py-3 text-sm font-semibold text-green-700 dark:text-green-300 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 sticky top-0 z-10 border-b border-green-200 dark:border-green-700 flex items-center">
                  <UserGroupIcon className="h-4 w-4 mr-2" />
                  Leads
                </div>
                {filteredPeople.filter((p) => p.type === "lead").length ===
                  0 && (
                  <div className="px-6 py-8 text-center">
                    <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">🔍</div>
                    <div className="text-gray-500 dark:text-gray-400 text-sm">
                      No leads found matching "{searchTerm}"
                    </div>
                  </div>
                )}
                {filteredPeople
                  .filter((p) => p.type === "lead")
                  .map((person) => (
                    <div
                      key={person.id}
                      className={`group flex items-center justify-between px-6 py-4 cursor-pointer border-l-4 transition-all duration-200 hover:shadow-md ${
                        selectedStudent?.id === person.id &&
                        selectedStudent?.type === person.type
                          ? "bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-l-green-500 text-gray-900 dark:text-white font-semibold shadow-md"
                          : "bg-white dark:bg-gray-700 border-l-transparent text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-l-gray-300 dark:hover:border-l-gray-500"
                      }`}
                      onClick={() => handleStudentSelect(person)}
                      style={{ minHeight: 56 }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900 dark:text-white text-base truncate">
                              {person.name}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                              Lead
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 mt-1">
                            {person.interest && (
                              <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                {person.interest}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-green-500 dark:group-hover:text-green-400 transition-colors" />
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        )}
        {/* Class and Event Selection */}
        {selectedStudent && (
          <>
            <div className="mt-8">
              <h2 className="text-lg font-semibold mb-4 dark:text-white text-center">
                Select a class or event for {selectedStudent.name}
              </h2>
              <div className="space-y-6">
                {/* Classes Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                    <ClockIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    Classes
                  </h3>
                  <div className="space-y-3">
                    {loadingAssignedClasses && selectedStudent?.type === "student" && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                        Loading assigned classes...
                      </div>
                    )}
                    {!loadingAssignedClasses && todaysClasses.length === 0 && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {selectedStudent?.type === "student" ? (
                          <div>
                            <p className="mb-2">No assigned classes scheduled for {getDayOfWeek(selectedDate, studioTimezone)}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Student has {selectedStudentAssignedClasses.length} assigned class(es) total
                            </p>
                          </div>
                        ) : (
                          "No classes scheduled for this day"
                        )}
                      </div>
                    )}
                    {!loadingAssignedClasses && todaysClasses.map((classItem) => {
                      // Check if student is already checked in for this class today
                      const isAlreadyCheckedIn = attendanceRecords.some((record) => {
                        const isSameDay = isAttendanceOnSelectedStudioDate(
                          record.attended_at,
                          studioTimezone,
                          selectedDate
                        );
                        const personMatches =
                          selectedStudent?.type === "student"
                            ? record.student_id === selectedStudent?.id
                            : record.lead_id === selectedStudent?.id;
                        return (
                          isSameDay &&
                          personMatches &&
                          record.class_id === classItem.id
                        );
                      });

                      return (
                        <button
                          key={classItem.id}
                          onClick={() => handleClassSelect(classItem)}
                          disabled={isAlreadyCheckedIn}
                          className={`w-full text-left p-4 rounded-lg transition-colors duration-200 border flex items-center justify-between ${
                            isAlreadyCheckedIn
                              ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 cursor-not-allowed"
                              : selectedClassOrEvent?.id === classItem.id && selectedType === "class"
                              ? "ring-2 ring-indigo-500 border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                              : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-200 dark:hover:border-indigo-500"
                          }`}
                        >
                          <div className="pointer-events-none">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {classItem.class_name}
                              {isAlreadyCheckedIn && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                  ✓ Checked In
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {formatTimeTo12Hour(classItem.start_time)} -{" "}
                              {formatTimeTo12Hour(classItem.end_time)}
                            </p>
                          </div>
                          {!isAlreadyCheckedIn && (
                            <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Events Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                    <UserGroupIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    Events
                  </h3>
                  <div className="space-y-3">
                    {todaysEvents.length === 0 && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No events scheduled for this day
                      </div>
                    )}
                    {todaysEvents.map((eventItem: any) => {
                      // Check if student is already checked in for this event today
                      const isAlreadyCheckedIn = attendanceRecords.some((record) => {
                        const isSameDay = isAttendanceOnSelectedStudioDate(
                          record.attended_at,
                          studioTimezone,
                          selectedDate
                        );
                        const personMatches =
                          selectedStudent?.type === "student"
                            ? record.student_id === selectedStudent?.id
                            : record.lead_id === selectedStudent?.id;
                        return (
                          isSameDay &&
                          personMatches &&
                          record.event_id === eventItem.id
                        );
                      });

                      return (
                        <button
                          key={eventItem.id}
                          onClick={() => handleEventSelect(eventItem)}
                          disabled={isAlreadyCheckedIn}
                          className={`w-full text-left p-4 rounded-lg transition-colors duration-200 border flex items-center justify-between ${
                            isAlreadyCheckedIn
                              ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 cursor-not-allowed"
                              : selectedClassOrEvent?.id === eventItem.id && selectedType === "event"
                              ? "ring-2 ring-green-500 border-green-500 bg-green-50 dark:bg-green-900/20"
                              : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-200 dark:hover:border-indigo-500"
                          }`}
                        >
                          <div className="pointer-events-none">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {eventItem.event_name}
                              {isAlreadyCheckedIn && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                  ✓ Checked In
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {formatTimeTo12Hour(eventItem.start_time)} -{" "}
                              {formatTimeTo12Hour(eventItem.end_time)}
                            </p>
                          </div>
                          {!isAlreadyCheckedIn && (
                            <ChevronRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Back button */}
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    onClick={handleKeypadClear}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm transition"
                  >
                    <ChevronLeftIcon className="h-4 w-4 pointer-events-none" />
                    Back
                  </button>
                </div>

              </div>
            </div>
          </>
        )}
        {/* Confirmation Modal */}
        {selectedStudent && selectedClassOrEvent && selectedType && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    onClick={() => {
                      setSelectedStudent(null);
                      setSelectedClassOrEvent(null);
                      setSelectedType(null);
                    }}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckCircleIcon
                      className="h-6 w-6 text-green-600 dark:text-green-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Confirm Check In
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedStudent.name} checking in for{" "}
                        {selectedType === "class"
                          ? selectedClassOrEvent.class_name
                          : selectedClassOrEvent.event_name}{" "}
                        at {formatTimeTo12Hour(selectedClassOrEvent.start_time)}{" "}
                        to {formatTimeTo12Hour(selectedClassOrEvent.end_time)}.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={isCheckingIn}
                    className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto ${
                      isCheckingIn
                        ? "bg-indigo-400 text-white cursor-not-allowed"
                        : "bg-indigo-600 text-white hover:bg-indigo-500"
                    }`}
                    onClick={handleConfirmCheckIn}
                  >
                    <span className="pointer-events-none">{isCheckingIn ? "Checking In..." : "Confirm"}</span>
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    onClick={() => {
                      setSelectedStudent(null);
                      setSelectedClassOrEvent(null);
                      setSelectedType(null);
                    }}
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Always show Logout Modal */}
        {showLogoutModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-sm w-full">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                Exit Kiosk Mode
              </h2>
              <p className="mb-2 text-gray-700 dark:text-gray-300">
                Enter the kiosk password to exit kiosk mode and return to the
                admin dashboard.
              </p>
              <input
                type="password"
                maxLength={MAX_LENGTHS.logoutPassword}
                value={logoutPassword}
                onChange={(e) => {
                  const sanitizedValue = sanitizeInput(e.target.value, 'logoutPassword');
                  if (sanitizedValue.length <= MAX_LENGTHS.logoutPassword) {
                    setLogoutPassword(sanitizedValue);
                  }
                }}
                className={getInputClassName(
                  "w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 mb-2"
                )}
                placeholder="Kiosk Password"
                autoFocus
              />
              {logoutError && (
                <div className="text-red-600 dark:text-red-400 text-sm mb-2">
                  {logoutError}
                </div>
              )}
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
                  onClick={() => {
                    setShowLogoutModal(false);
                    setLogoutPassword("");
                    setLogoutError("");
                  }}
                  disabled={logoutLoading}
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={async () => {
                    setLogoutError("");
                    setLogoutLoading(true);
                    try {
                      if (!kioskStudioId) throw new Error("Studio not found");

                      // Enhanced password validation
                      if (!validateInput(logoutPassword, 'logoutPassword')) {
                        logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'logoutPassword', value: logoutPassword.substring(0, 10) + '...' });
                        setLogoutError("Invalid password format. Please check your input.");
                        setLogoutLoading(false);
                        return;
                      }

                      // Use the current auth token from AuthContext for kiosk logout
                      // This handles both main accounts and sub-accounts properly
                      const token = getAuthToken();
                      if (!token) {
                        setLogoutError(
                          "You must be logged in to exit kiosk mode."
                        );
                        setLogoutLoading(false);
                        return;
                      }

                      const response = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${kioskStudioId}/verify-kiosk-password`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                            "X-CSRF-Token": csrfToken,
                          },
                          body: JSON.stringify({
                            password: logoutPassword,
                            csrfToken,
                          }),
                        }
                      );
                      const data = await response.json();
                      if (!response.ok) {
                        throw new Error(
                          data.error || "Failed to verify password"
                        );
                      }
                      if (!data.isMatch) {
                        setLogoutError("Incorrect kiosk password.");
                        setLogoutLoading(false);
                        return;
                      }
                      setShowLogoutModal(false);
                      setLogoutPassword("");
                      setLogoutError("");
                      setLogoutLoading(false);
                      sessionStorage.removeItem("lockStudentSignIn");

                      // Add a small delay to ensure sessionStorage is cleared
                      // and state is properly updated before navigation
                      setTimeout(() => {
                        navigate("/app", { replace: true });
                      }, 100);
                    } catch (err) {
                      logSecurityEvent('LOGOUT_ERROR', { 
                        error: err instanceof Error ? err.message : 'Unknown error',
                        studioId: kioskStudioId
                      });
                      setLogoutError(
                        err instanceof Error
                          ? err.message
                          : "Error verifying password."
                      );
                      setLogoutLoading(false);
                    }
                  }}
                  disabled={logoutLoading}
                >
                  <span className="pointer-events-none">{logoutLoading ? "Logging out..." : "Logout"}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
